import React, { useState } from "react";
import styled from "styled-components";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import "aos/dist/aos.css";

const TestimonialsWrapper = styled.div`
  background: #fff;
  padding: 80px 20px;

  .container {
    max-width: 900px;
    margin: 0 auto;
  }

  .testimonials-carousel {
    position: relative;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 30px;
  }

  .carousel-arrow {
    font-size: 28px;
    color: #000;
    cursor: pointer;
    transition: all 0.3s ease;
    padding: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    &:hover {
      color: #667eea;
      transform: scale(1.2);
    }
  }

  .testimonial-content {
    flex: 1;
    animation: slideIn 0.5s ease-out;
  }

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateX(20px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  .quote-mark {
    font-size: 80px;
    font-weight: bold;
    color: #000;
    line-height: 1;
    margin-bottom: 20px;
  }

  .testimonial-text {
    font-size: 18px;
    color: #333;
    line-height: 1.8;
    margin-bottom: 40px;
    font-family: "Outfit", sans-serif;
    font-weight: 400;
  }

  .testimonial-author {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 15px;

    .author-image {
      width: 80px;
      height: 80px;
      border-radius: 50%;
      overflow: hidden;
      object-fit: cover;
      border: 2px solid #e8e8e8;
    }

    .author-info {
      .author-name {
        font-size: 16px;
        font-weight: 700;
        color: #000;
        margin: 0;
        font-family: "Outfit", sans-serif;
      }

      .author-label {
        font-size: 13px;
        color: #999;
        margin: 5px 0 0 0;
        font-family: "Outfit", sans-serif;
      }
    }
  }

  .carousel-controls {
    display: none;
  }

  @media (max-width: 768px) {
    padding: 60px 20px;

    .quote-mark {
      font-size: 60px;
      margin-bottom: 15px;
    }

    .testimonial-text {
      font-size: 16px;
      margin-bottom: 30px;
      line-height: 1.6;
    }

    .testimonial-author {
      .author-image {
        width: 70px;
        height: 70px;
      }

      .author-info {
        .author-name {
          font-size: 15px;
        }

        .author-label {
          font-size: 12px;
        }
      }
    }

    .carousel-controls {
      gap: 20px;
      margin-top: 40px;

      .nav-arrow {
        width: 36px;
        height: 36px;
        font-size: 14px;
      }
    }
  }

  @media (max-width: 576px) {
    padding: 40px 15px;

    .quote-mark {
      font-size: 50px;
      margin-bottom: 12px;
    }

    .testimonial-text {
      font-size: 14px;
      margin-bottom: 25px;
    }

    .carousel-controls {
      gap: 15px;
    }
  }
`;

const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const testimonials = [
    {
      text: "Amazing quality and fast shipping! The products exceeded my expectations and the customer service was incredibly helpful.",
      author: "Sarah Johnson",
      image:
        "https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80",
    },
    {
      text: "The fashion collection is absolutely stunning! Each piece is carefully curated and the prices are very reasonable. Shopping here is always a pleasant experience.",
      author: "Emily Chen",
      image:
        "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80",
    },
    {
      text: "Exceptional quality and style! The website is easy to navigate and the checkout process is smooth. I received my order in perfect condition. Highly recommended!",
      author: "Michael Rodriguez",
      image:
        "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80",
    },
    {
      text: "I love the unique designs and premium quality. The return policy is hassle-free and the support team is always ready to help. Will definitely shop again!",
      author: "Jessica Williams",
      image:
        "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80",
    },
    {
      text: "Best online shopping experience I've had! The products are exactly as described and arrived much faster than expected. Customer service was fantastic!",
      author: "David Thompson",
      image:
        "https://images.unsplash.com/photo-1507551557519-14a2fbf17039?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80",
    },
  ];

  const handlePrev = () => {
    setCurrentIndex((prev) =>
      prev === 0 ? testimonials.length - 1 : prev - 1,
    );
  };

  const handleNext = () => {
    setCurrentIndex((prev) =>
      prev === testimonials.length - 1 ? 0 : prev + 1,
    );
  };

  const current = testimonials[currentIndex];

  return (
    <TestimonialsWrapper>
      <div className="container">
        <div className="testimonials-carousel">
          <div className="carousel-arrow" onClick={handlePrev}>
            <LeftOutlined />
          </div>

          <div
            className="testimonial-content"
            key={currentIndex}
            data-aos="fade-in"
          >
            <div className="quote-mark">"</div>
            <p className="testimonial-text">{current.text}</p>

            <div className="testimonial-author">
              <img
                src={current.image}
                alt={current.author}
                className="author-image"
              />
              <div className="author-info">
                <p className="author-name">{current.author}</p>
                <p className="author-label">Customer Review</p>
              </div>
            </div>
          </div>

          <div className="carousel-arrow" onClick={handleNext}>
            <RightOutlined />
          </div>
        </div>
      </div>
    </TestimonialsWrapper>
  );
};

export default Testimonials;
